import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fileUrl } from '@/lib/api';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { ArrowUpRight, MapPin, Camera, Users, Sparkles, Mountain, Waves, Wind, Wifi, Crown, Heart, ImagePlus, Link as LinkIcon, Video } from 'lucide-react';

function topVideoEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return `https://www.youtube.com/embed${u.pathname}`;
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
  } catch (_e) { return null; }
  return null;
}

export default function Landing() {
  const [game, setGame] = useState(null);
  const [convoys, setConvoys] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [topMedia, setTopMedia] = useState([]);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const { t } = useI18n();

  useEffect(() => {
    api.get('/games/fh6').then(({ data }) => setGame(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchLive = () => {
      api.get('/convoys').then(({ data }) => setConvoys(data)).catch(() => {});
      api.get('/presence/online').then(({ data }) => setOnlineCount(data.count)).catch(() => {});
    };
    fetchLive();
    const i = setInterval(fetchLive, 20000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    api.get('/media/top?limit=3').then(({ data }) => setTopMedia(data || [])).catch(() => {});
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    if (topMedia.length <= 1) return;
    const i = setInterval(() => setCarouselIdx((idx) => (idx + 1) % topMedia.length), 6000);
    return () => clearInterval(i);
  }, [topMedia.length]);

  return (
    <div className="min-h-screen pt-24 pb-24 px-6 relative">
      {/* HERO */}
      <section className="max-w-7xl mx-auto relative">
        <div className="grid lg:grid-cols-[1.1fr,0.9fr] gap-10 items-center">
          {/* Left text */}
          <div>
            <div className="flex items-center gap-3 fz-rise flex-wrap">
              <div
                data-testid="landing-live-online"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/5"
              >
                <span className="relative flex w-2 h-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                <span className="text-xs font-bold text-emerald-300">{onlineCount}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-200/70">{t('landing.live.online')}</span>
              </div>
              <div
                data-testid="landing-live-convoys"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF007F]/30 bg-[#FF007F]/5"
              >
                <Wifi className="w-3 h-3 text-[#FF007F] animate-pulse" />
                <span className="text-xs font-bold text-[#FF007F]">{convoys.length}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#FF007F]/70">{t('landing.live.convoys')}</span>
              </div>
            </div>

            <h1 className="font-display text-[clamp(2.5rem,6.5vw,5.5rem)] font-black uppercase tracking-tighter leading-[0.95] mt-4 fz-rise fz-rise-delay-1">
              {t('hero.title.line1')} <br/>
              <span className="text-[#FF007F]">Forza</span> <span className="text-[#00F0FF]">Horizon 6</span>.<br/>
              <span className="text-white/40">{t('hero.title.line2')}</span>
            </h1>

            <p className="mt-6 max-w-xl text-white/70 text-lg fz-rise fz-rise-delay-2">
              {t('hero.subtitle')}
            </p>

            <div className="mt-8 flex flex-wrap gap-3 fz-rise fz-rise-delay-3">
              <Link to="/maps/fh6">
                <Button
                  data-testid="hero-enter-map-btn"
                  className="h-12 px-7 rounded-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide group"
                >
                  {t('hero.cta.map')}
                  <ArrowUpRight className="w-4 h-4 ml-2 group-hover:rotate-45 transition-transform" />
                </Button>
              </Link>
              <a href="#how">
                <Button
                  variant="ghost"
                  className="h-12 px-7 rounded-full border border-white/10 text-white hover:bg-white/5"
                >
                  {t('hero.cta.about')}
                </Button>
              </a>
            </div>

            {/* Stats strip */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 fz-rise fz-rise-delay-4">
              {[
                { icon: MapPin, label: t('hero.stats.map'), sub: t('hero.stats.map_sub') },
                { icon: Camera, label: t('hero.stats.photos'), sub: t('hero.stats.photos_sub') },
                { icon: Users, label: t('hero.stats.meets'), sub: t('hero.stats.meets_sub') },
                { icon: Sparkles, label: t('hero.stats.likes'), sub: t('hero.stats.likes_sub') },
              ].map((step) => (
                <div key={step.label} className="glass rounded-2xl p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/5 grid place-items-center">
                    <step.icon className="w-4 h-4 text-[#00F0FF]" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-sm">{step.label}</div>
                    {step.sub && <div className="text-[11px] text-white/50">{step.sub}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Featured map card */}
          <div className="fz-rise fz-rise-delay-2">
            <Link
              to="/maps/fh6"
              data-testid="featured-map-card"
              className="block relative aspect-[776/970] rounded-3xl overflow-hidden border border-white/10 hover:border-[#FF007F]/50 transition-all duration-500 group"
            >
              {game?.cover_url && (
                <img
                  src={game.cover_url}
                  alt="Forza Horizon 6 Map"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_70%_30%,rgba(255,0,127,0.25),transparent_60%)]" />

              <div className="absolute top-4 left-4 flex items-center gap-2">
                <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border border-[#FF007F] text-[#FF007F] bg-black/50 backdrop-blur-md">
                  {t('landing.featured.year')}
                </span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="text-xs text-white/60 uppercase tracking-wider font-semibold">
                  {game?.location || 'Japon'}
                </div>
                <div className="font-display font-black text-3xl lg:text-4xl uppercase tracking-tighter leading-none mt-1">
                  Forza Horizon 6
                </div>
                <div className="text-sm text-white/70 mt-2 max-w-md">{game?.tagline}</div>
                <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#00F0FF] font-bold">
                  {t('landing.featured.cta')} <ArrowUpRight className="w-3 h-3 group-hover:rotate-45 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* TERRAINS */}
      <section className="max-w-7xl mx-auto mt-28">
        <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
          <div>
            <h2 className="font-display text-3xl lg:text-5xl font-black uppercase tracking-tighter">
              {t('landing.terrains.title1')} <br className="hidden md:block" />
              <span className="text-white/40">{t('landing.terrains.title2')}</span>
            </h2>
          </div>
          <p className="max-w-md text-white/60 text-sm">
            {t('landing.terrains.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Mountain, title: t('landing.terrains.mountains.t'), desc: t('landing.terrains.mountains.d'), color: '#00F0FF' },
            { icon: Wind, title: t('landing.terrains.forests.t'), desc: t('landing.terrains.forests.d'), color: '#10B981' },
            { icon: Waves, title: t('landing.terrains.city.t'), desc: t('landing.terrains.city.d'), color: '#FF007F' },
          ].map((terrain) => (
            <div key={terrain.title} className="glass rounded-2xl p-6 hover:border-white/30 transition-all">
              <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: `${terrain.color}22`, color: terrain.color }}>
                <terrain.icon className="w-5 h-5" />
              </div>
              <div className="font-display font-bold text-xl mt-4 uppercase tracking-tight">{terrain.title}</div>
              <div className="text-white/60 text-sm mt-2">{terrain.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LIVE CONVOYS */}
      <section className="max-w-7xl mx-auto mt-28" id="convoys">
        <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/5 text-[#00F0FF] text-xs uppercase tracking-[0.25em] font-bold">
              <Wifi className="w-3 h-3 animate-pulse" /> {onlineCount} {t('landing.live.online')} · {convoys.length} {t('landing.live.convoys')}
            </div>
            <h2 className="font-display text-3xl lg:text-5xl font-black uppercase tracking-tighter mt-3">
              {t('landing.convoys.title')}
            </h2>
            <p className="text-white/60 mt-2 max-w-xl">{t('landing.convoys.subtitle')}</p>
          </div>
          <Link to="/convoys">
            <Button
              data-testid="landing-convoys-cta"
              className="rounded-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide h-11 px-5"
            >
              {t('landing.convoys.cta')} <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {convoys.length === 0 ? (
          <div data-testid="landing-convoys-empty" className="glass rounded-2xl p-10 text-center">
            <Users className="w-8 h-8 mx-auto text-white/30" />
            <div className="text-white/70 font-display font-bold text-lg mt-3">{t('landing.convoys.empty')}</div>
            <Link to="/convoys" className="inline-block mt-4">
              <Button className="rounded-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide">
                {t('landing.convoys.create')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {convoys.slice(0, 6).map((c) => {
              const seasonIcon = { spring: '🌸', summer: '☀️', autumn: '🍂', winter: '❄️' }[c.season] || '☀️';
              return (
                <Link
                  key={c.id}
                  to={`/convoys/${c.id}`}
                  data-testid={`landing-convoy-${c.id}`}
                  className="group glass rounded-2xl p-5 hover:border-white/30 transition-all relative overflow-hidden"
                >
                  <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-20 bg-[#FF007F] group-hover:opacity-40 transition-opacity" />
                  <div className="relative">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">
                      {seasonIcon} {t(`common.season.${c.season}`)}
                    </div>
                    <h3 className="font-display font-black text-xl uppercase tracking-tighter mt-1 line-clamp-1">{c.name}</h3>
                    <div className="text-xs text-white/50 mt-1 inline-flex items-center gap-1">
                      <Crown className="w-3 h-3 text-[#FFD479]" /> {c.host_name}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-white/40" />
                        <div className="flex -space-x-2">
                          {c.members.slice(0, 4).map((m) => (
                            <div key={m.user_id} className="w-6 h-6 rounded-full bg-[#00F0FF] text-black border-2 border-black grid place-items-center text-[9px] font-bold">
                              {m.avatar_url ? <img src={m.avatar_url} alt={m.name} className="w-full h-full rounded-full" /> : (m.name?.[0]?.toUpperCase() || '?')}
                            </div>
                          ))}
                        </div>
                        <span className="text-xs text-white/50">{c.members_count}/{c.max_size}</span>
                      </div>
                      <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-white group-hover:rotate-45 transition-all" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-5xl mx-auto mt-32 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-xs uppercase tracking-[0.25em] text-white/70">
          <Sparkles className="w-3 h-3" /> {t('hero.cta.about')}
        </div>
        <h3 className="font-display text-3xl lg:text-5xl font-black uppercase tracking-tighter mt-6">
          {t('landing.how.title')}
        </h3>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { n: '01', t: t('landing.how.s1.t'), d: t('landing.how.s1.d') },
            { n: '02', t: t('landing.how.s2.t'), d: t('landing.how.s2.d') },
            { n: '03', t: t('landing.how.s3.t'), d: t('landing.how.s3.d') },
            { n: '04', t: t('landing.how.s4.t'), d: t('landing.how.s4.d') },
          ].map((step) => (
            <div key={step.n} className="glass rounded-2xl p-6 text-left">
              <div className="font-display font-black text-5xl text-[#00F0FF]/40">{step.n}</div>
              <div className="font-display font-bold text-xl mt-2">{step.t}</div>
              <div className="text-white/60 text-sm mt-2">{step.d}</div>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <Link to="/maps/fh6">
            <Button
              data-testid="cta-enter-map-btn"
              className="h-14 px-8 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-black hover:opacity-90 font-black uppercase tracking-wide text-base"
            >
              {t('hero.cta.map')} de FH6 <ArrowUpRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* TOP MEDIA CAROUSEL */}
      {topMedia.length > 0 && (
        <section className="max-w-6xl mx-auto mt-32" data-testid="landing-top-media">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F] text-xs uppercase tracking-[0.25em] font-bold">
                <ImagePlus className="w-3 h-3" /> {t('landing.top_media.label') || 'Trending'}
              </div>
              <h3 className="font-display text-3xl lg:text-5xl font-black uppercase tracking-tighter mt-3">
                {t('landing.top_media.title') || 'Les meilleurs clichés'}
              </h3>
              <p className="text-white/60 text-sm mt-2">{t('landing.top_media.subtitle') || 'Le top 3 du moment, par la communauté.'}</p>
            </div>
            <Link to="/media" className="text-[#00F0FF] hover:underline text-sm font-bold uppercase tracking-wider inline-flex items-center gap-1" data-testid="landing-top-media-link">
              {t('landing.top_media.see_all') || 'Voir tout'} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative">
            <div className="grid md:grid-cols-3 gap-5">
              {topMedia.map((m, i) => {
                const embed = m.kind === 'link' ? topVideoEmbed(m.external_url) : null;
                const isPrimary = i === carouselIdx;
                return (
                  <Link
                    to="/media"
                    key={m.id}
                    data-testid={`landing-top-media-card-${i}`}
                    className={`group glass rounded-2xl overflow-hidden flex flex-col transition-all ${
                      isPrimary ? 'ring-2 ring-[#FF007F]/40 scale-[1.02]' : 'opacity-90 hover:opacity-100'
                    }`}
                  >
                    <div className="relative aspect-video bg-black/40">
                      {m.kind === 'image' && m.image_path && (
                        <img src={fileUrl(m.image_path)} alt={m.title} className="w-full h-full object-cover" />
                      )}
                      {m.kind === 'video' && m.video_path && (
                        <video src={fileUrl(m.video_path)} muted className="w-full h-full object-cover" />
                      )}
                      {m.kind === 'link' && embed && (
                        <img
                          src={embed.includes('youtube.com/embed') ? `https://img.youtube.com/vi/${embed.split('/embed/')[1]?.split('?')[0]}/hqdefault.jpg` : ''}
                          alt={m.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      {m.kind === 'link' && !embed && (
                        <div className="w-full h-full grid place-items-center text-[#00F0FF]">
                          <LinkIcon className="w-8 h-8" />
                        </div>
                      )}
                      {/* Rank badge */}
                      <div className="absolute top-2 left-2 w-8 h-8 rounded-full bg-black/80 border-2 border-[#FFD479] grid place-items-center font-display text-sm font-black text-[#FFD479]">
                        #{i + 1}
                      </div>
                      {/* Kind badge */}
                      <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-black/80 text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1">
                        {m.kind === 'image' && <Camera className="w-3 h-3" />}
                        {m.kind === 'video' && <Video className="w-3 h-3" />}
                        {m.kind === 'link' && <LinkIcon className="w-3 h-3" />}
                      </div>
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <h4 className="font-bold text-sm leading-tight line-clamp-2">{m.title}</h4>
                      <div className="text-[11px] text-white/50 mt-1">{m.author_name}</div>
                      <div className="mt-auto flex items-center gap-3 text-xs text-white/60 pt-3">
                        <span className="inline-flex items-center gap-1 text-[#FF007F]"><Heart className="w-3 h-3 fill-current" /> {m.likes_count}</span>
                        <span>· {m.comments_count || 0} 💬</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CREATOR SOCIAL LINKS */}
      <section className="max-w-5xl mx-auto mt-32 text-center" id="creator">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-xs uppercase tracking-[0.25em] text-white/70">
          {t('landing.creator.title')}
        </div>
        <h3 className="font-display text-2xl lg:text-4xl font-black uppercase tracking-tighter mt-5">
          Ochibi <span className="text-white/40">— FH6 community</span>
        </h3>
        <p className="text-white/60 text-sm mt-2">{t('landing.creator.subtitle')}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href="https://www.twitch.tv/ochibitv"
            target="_blank" rel="noopener noreferrer"
            data-testid="creator-twitch"
            className="group glass rounded-2xl px-6 py-4 inline-flex items-center gap-3 hover:border-[#9146FF]/40 transition-all"
          >
            <div className="w-10 h-10 rounded-lg grid place-items-center bg-[#9146FF]/20 group-hover:bg-[#9146FF]/40 transition">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#9146FF]" aria-hidden>
                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
              </svg>
            </div>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#9146FF] font-bold">Twitch</div>
              <div className="text-sm font-bold text-white">@ochibitv</div>
            </div>
          </a>
          <a
            href="https://www.tiktok.com/@_ochibi"
            target="_blank" rel="noopener noreferrer"
            data-testid="creator-tiktok"
            className="group glass rounded-2xl px-6 py-4 inline-flex items-center gap-3 hover:border-[#00F2EA]/40 transition-all"
          >
            <div className="w-10 h-10 rounded-lg grid place-items-center bg-[#00F2EA]/20 group-hover:bg-[#00F2EA]/40 transition">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#00F2EA]" aria-hidden>
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
              </svg>
            </div>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F2EA] font-bold">TikTok</div>
              <div className="text-sm font-bold text-white">@_ochibi</div>
            </div>
          </a>
          <a
            href="https://www.youtube.com/@ochibitv6585"
            target="_blank" rel="noopener noreferrer"
            data-testid="creator-youtube"
            className="group glass rounded-2xl px-6 py-4 inline-flex items-center gap-3 hover:border-[#FF0000]/40 transition-all"
          >
            <div className="w-10 h-10 rounded-lg grid place-items-center bg-[#FF0000]/20 group-hover:bg-[#FF0000]/40 transition">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#FF0000]" aria-hidden>
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </div>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF0000] font-bold">YouTube</div>
              <div className="text-sm font-bold text-white">@ochibitv6585</div>
            </div>
          </a>
          <a
            href="https://discord.gg/jNpetafBMk"
            target="_blank" rel="noopener noreferrer"
            data-testid="creator-discord"
            className="group glass rounded-2xl px-6 py-4 inline-flex items-center gap-3 hover:border-[#5865F2]/40 transition-all"
          >
            <div className="w-10 h-10 rounded-lg grid place-items-center bg-[#5865F2]/20 group-hover:bg-[#5865F2]/40 transition">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#5865F2]" aria-hidden>
                <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3a14.5 14.5 0 0 0-.65 1.323 18.7 18.7 0 0 0-5.516 0A14.4 14.4 0 0 0 9.74 3 19.74 19.74 0 0 0 5.98 4.37C2.546 9.46 1.624 14.413 2.083 19.295a19.9 19.9 0 0 0 6.073 3.04c.486-.65.918-1.34 1.292-2.069a13 13 0 0 1-2.034-.969c.17-.124.337-.252.498-.385 3.97 1.825 8.262 1.825 12.18 0 .164.133.33.261.501.385a13 13 0 0 1-2.037.97c.374.728.806 1.418 1.292 2.068a19.85 19.85 0 0 0 6.075-3.04c.55-5.68-.94-10.59-3.604-14.926ZM8.02 16.367c-1.182 0-2.156-1.085-2.156-2.418s.952-2.42 2.156-2.42c1.205 0 2.18 1.087 2.156 2.42 0 1.333-.952 2.418-2.156 2.418Zm7.96 0c-1.182 0-2.156-1.085-2.156-2.418s.952-2.42 2.156-2.42c1.205 0 2.18 1.087 2.156 2.42 0 1.333-.951 2.418-2.156 2.418Z"/>
              </svg>
            </div>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5865F2] font-bold">Discord</div>
              <div className="text-sm font-bold text-white">{t('landing.creator.discord_join') || 'Rejoindre le serveur'}</div>
            </div>
          </a>
        </div>
      </section>
    </div>
  );
}
